/**
 * Admin Redeem Group Management Page
 * Provides interface for managing redeem groups with CRUD operations
 * Uses TanStack Table for data display, search, and actions
 */

import { useState, useMemo } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  type SortingState,
} from '@tanstack/react-table';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import {
  useRedeemGroupsList,
  useDeleteRedeemGroup,
} from '../../hooks/useRedeemGroupApi';
import { type RedeemGroup } from '../../lib/types/redeemGroup';
import { RedeemGroupFormModal } from '../../components/admin/RedeemGroupFormModal';
import { CodeGenerationModal } from '../../components/admin/CodeGenerationModal';
import { CodeRetrievalModal } from '../../components/admin/CodeRetrievalModal';
import { CodeImportModal } from '../../components/admin/CodeImportModal';
import { CodeStatusChecker } from '../../components/admin/CodeStatusChecker';
import { ActionMenu } from '../../components/ui/ActionMenu';

const columnHelper = createColumnHelper<RedeemGroup>();

export function RedeemGroupListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedGroup, setSelectedGroup] = useState<RedeemGroup | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<RedeemGroup | null>(null);
  const [groupToGenerateCodes, setGroupToGenerateCodes] =
    useState<RedeemGroup | null>(null);
  const [groupToViewCodes, setGroupToViewCodes] = useState<RedeemGroup | null>(
    null
  );
  const [groupToImportCodes, setGroupToImportCodes] =
    useState<RedeemGroup | null>(null);

  // Fetch redeem groups with infinite query
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useRedeemGroupsList(50);

  const deleteGroup = useDeleteRedeemGroup();

  // Flatten all pages into single array
  const allGroups = useMemo(() => {
    return data?.pages.flatMap(page => page.groups) || [];
  }, [data?.pages]);

  // Filter groups based on search query (searches across ALL loaded pages)
  const filteredGroups = useMemo(() => {
    if (!allGroups.length || !searchQuery) {
      return allGroups;
    }

    const query = searchQuery.toLowerCase();
    return allGroups.filter((group: RedeemGroup) =>
      group.name.toLowerCase().includes(query)
    );
  }, [allGroups, searchQuery]);

  // Table columns definition
  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Group Name',
        cell: ({ getValue, row }) => (
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {getValue()}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {row.original.groupId}
            </p>
          </div>
        ),
        enableSorting: true,
      }),
      columnHelper.accessor('enabled', {
        header: 'Status',
        cell: ({ getValue }) => (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              getValue()
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
            }`}
          >
            {getValue() ? 'Enabled' : 'Disabled'}
          </span>
        ),
        enableSorting: true,
      }),
      columnHelper.accessor('totalCodes', {
        header: 'Total Codes',
        cell: ({ getValue }) => (
          <span className="text-sm text-gray-900 dark:text-gray-100">
            {getValue().toLocaleString()}
          </span>
        ),
        enableSorting: true,
      }),
      columnHelper.accessor('redeemedCodes', {
        header: 'Redeemed',
        cell: ({ getValue, row }) => {
          const redeemed = getValue();
          const total = row.original.totalCodes;
          const percentage =
            total > 0 ? Math.round((redeemed / total) * 100) : 0;

          return (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-900 dark:text-gray-100">
                {redeemed.toLocaleString()}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                ({percentage}%)
              </span>
            </div>
          );
        },
        enableSorting: true,
      }),
      columnHelper.accessor('created_at', {
        header: 'Created',
        cell: ({ getValue }) => {
          const date = new Date(getValue());
          return (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {date.toLocaleDateString()}
            </span>
          );
        },
        enableSorting: true,
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setGroupToViewCodes(row.original)}
              className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300 text-sm font-medium"
            >
              View Codes
            </button>
            <ActionMenu
              ariaLabel={`Actions for ${row.original.name}`}
              items={[
                {
                  label: 'Generate',
                  onClick: () => setGroupToGenerateCodes(row.original),
                  colorClass: 'text-green-600',
                  hoverColorClass: 'hover:text-green-900',
                  darkColorClass: 'dark:text-green-400',
                  darkHoverColorClass: 'dark:hover:text-green-300',
                },
                {
                  label: 'Import',
                  onClick: () => setGroupToImportCodes(row.original),
                  colorClass: 'text-orange-600',
                  hoverColorClass: 'hover:text-orange-900',
                  darkColorClass: 'dark:text-orange-400',
                  darkHoverColorClass: 'dark:hover:text-orange-300',
                },
                {
                  label: 'Edit',
                  onClick: () => setSelectedGroup(row.original),
                  colorClass: 'text-sky-600',
                  hoverColorClass: 'hover:text-sky-900',
                  darkColorClass: 'dark:text-sky-400',
                  darkHoverColorClass: 'dark:hover:text-sky-300',
                },
                {
                  label: 'Delete',
                  onClick: () => setGroupToDelete(row.original),
                  colorClass: 'text-red-600',
                  hoverColorClass: 'hover:text-red-900',
                  darkColorClass: 'dark:text-red-400',
                  darkHoverColorClass: 'dark:hover:text-red-300',
                },
              ]}
            />
          </div>
        ),
      }),
    ],
    []
  );

  // Initialize table
  const table = useReactTable({
    data: filteredGroups,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Handle search input
  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const query = formData.get('search') as string;
    setSearchQuery(query.trim());
  };

  const handleCreateGroup = () => {
    setShowCreateModal(true);
  };

  const handleDeleteGroup = async (groupId: string) => {
    try {
      await deleteGroup.mutateAsync(groupId);
      setGroupToDelete(null);
    } catch (_error) {
      // Error is handled by the mutation
    }
  };

  const handleLoadMore = () => {
    fetchNextPage();
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Redeem Groups
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage redeem code groups and their configurations
            </p>
          </div>
          <button
            onClick={handleCreateGroup}
            className="self-start inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Create Group
          </button>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-sky-100 dark:bg-sky-900 rounded-full flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-sky-600 dark:text-sky-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Total Groups
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {data?.pages[0]?.totalGroups || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-green-600 dark:text-green-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Active Groups
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {allGroups.filter(g => g.enabled).length || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-purple-600 dark:text-purple-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Total Codes
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {allGroups
                        .reduce((sum, g) => sum + g.totalCodes, 0)
                        .toLocaleString() || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Code Status Checker Section */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Check Code Status
          </h2>
          <CodeStatusChecker />
        </div>

        {/* Search and Filters */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search Form */}
              <form onSubmit={handleSearch} className="flex-1">
                <div className="flex">
                  <input
                    type="text"
                    name="search"
                    placeholder="Search groups by name..."
                    defaultValue={searchQuery}
                    className="block w-full rounded-l-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                  />
                  <button
                    type="submit"
                    className="inline-flex items-center px-4 py-2 border border-l-0 border-gray-300 dark:border-gray-600 rounded-r-md bg-gray-50 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </button>
                </div>
              </form>

              {/* Clear Search */}
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Results Info */}
          <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {isLoading ? (
                  'Loading...'
                ) : error ? (
                  <span className="text-red-600 dark:text-red-400">
                    Error loading groups
                  </span>
                ) : (
                  <>
                    Showing{' '}
                    <span className="font-medium">{filteredGroups.length}</span>{' '}
                    of{' '}
                    <span className="font-medium">
                      {data?.pages[0]?.totalGroups || 0}
                    </span>{' '}
                    groups
                    {allGroups.length < (data?.pages[0]?.totalGroups || 0) && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({allGroups.length} loaded)
                      </span>
                    )}
                  </>
                )}
              </p>
              {error && (
                <button
                  onClick={() => refetch()}
                  className="text-sm font-medium text-sky-600 hover:text-sky-500 dark:text-sky-400 dark:hover:text-sky-300"
                >
                  Retry
                </button>
              )}
            </div>
          </div>

          {/* Groups Table */}
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <LoadingSpinner size="lg" />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <p className="text-gray-500 dark:text-gray-400 mb-2">
                    Failed to load groups
                  </p>
                  <button
                    onClick={() => refetch()}
                    className="text-sky-600 hover:text-sky-500 dark:text-sky-400 dark:hover:text-sky-300 font-medium"
                  >
                    Try again
                  </button>
                </div>
              </div>
            ) : !filteredGroups.length ? (
              <div className="flex items-center justify-center h-64">
                <p className="text-gray-500 dark:text-gray-400">
                  {searchQuery
                    ? 'No groups match your search'
                    : 'No groups found'}
                </p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th
                          key={header.id}
                          className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <div className="flex items-center space-x-1">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {header.column.getCanSort() && (
                              <span className="text-gray-400">
                                {{
                                  asc: '↑',
                                  desc: '↓',
                                }[header.column.getIsSorted() as string] ??
                                  '↕️'}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {table.getRowModel().rows.map(row => (
                    <tr
                      key={row.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      {row.getVisibleCells().map(cell => (
                        <td
                          key={cell.id}
                          className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Load More Button */}
          {hasNextPage && !isLoading && !error && (
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
              <button
                onClick={handleLoadMore}
                disabled={isFetchingNextPage}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isFetchingNextPage ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Group Create/Edit Modal */}
      <RedeemGroupFormModal
        isOpen={showCreateModal || !!selectedGroup}
        onClose={() => {
          setShowCreateModal(false);
          setSelectedGroup(null);
        }}
        group={selectedGroup}
      />

      {/* Code Generation Modal */}
      {groupToGenerateCodes && (
        <CodeGenerationModal
          isOpen={true}
          onClose={() => setGroupToGenerateCodes(null)}
          groupId={groupToGenerateCodes.groupId}
          groupName={groupToGenerateCodes.name}
        />
      )}

      {/* Code Retrieval Modal */}
      {groupToViewCodes && (
        <CodeRetrievalModal
          isOpen={true}
          onClose={() => setGroupToViewCodes(null)}
          groupId={groupToViewCodes.groupId}
          groupName={groupToViewCodes.name}
        />
      )}

      {/* Code Import Modal */}
      {groupToImportCodes && (
        <CodeImportModal
          isOpen={true}
          onClose={() => setGroupToImportCodes(null)}
          groupId={groupToImportCodes.groupId}
          groupName={groupToImportCodes.name}
        />
      )}

      {/* Delete Confirmation Modal */}
      {groupToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Delete Redeem Group
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to delete this redeem group? This will not
              delete the generated codes, but users will no longer be able to
              redeem them. This action cannot be undone.
            </p>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              Group: {groupToDelete.name}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              ID: {groupToDelete.groupId}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setGroupToDelete(null)}
                disabled={deleteGroup.isPending}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteGroup(groupToDelete.groupId)}
                disabled={deleteGroup.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
              >
                {deleteGroup.isPending ? 'Deleting...' : 'Delete Group'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
